import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  MenuItem,
  Select,
  Stack,
  TextField as MUITextField,
} from "@mui/material";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Modal from "components/basic/modal";
import { formatMoney } from "utils/formatMoney";

import { genId } from "../../../setAvailability/helpers";
import { DATE_FORMAT } from "../../../setAvailability/constants";
import { BookingType } from "../../../eventTypes";
import AttendeeInput from "../AttendeeInput";
import QuoteDetailsDialog from "../QuoteDetailsDialog";
import Field from "./components/Field";
import ModalHeader from "./components/ModalHeader";
import DateTimeSection from "./components/DateTimeSection";
import EventTypeSelect from "./components/EventTypeSelect";
import AttachmentsSection from "./components/AttachmentsSection";
import ModalFooter from "./components/ModalFooter";
import {
  Attendee,
  AttendeeOption,
  Booking,
  BookingAttachment,
  LeadCustomer,
} from "../../types";

interface BookingModalProps {
  existing?: Booking | null;
  defaultDate?: string;
  defaultFrom?: string;
  defaultTo?: string;
  leadSuggestions: AttendeeOption[];
  leadsByEmail?: Map<string, LeadCustomer>;
  saving?: boolean;
  onClose: () => void;
  onSave: (booking: Booking, newFiles: File[]) => void;
}

const BookingModal: React.FC<BookingModalProps> = ({
  existing,
  defaultDate,
  defaultFrom,
  defaultTo,
  leadSuggestions,
  leadsByEmail,
  saving = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation("calendar");
  const isEdit = Boolean(existing);
  const [viewingQuotes, setViewingQuotes] = useState<LeadCustomer | null>(null);

  const [title, setTitle] = useState(existing?.title ?? "");
  const [type, setType] = useState<BookingType>(existing?.type ?? "onsite");
  const [date, setDate] = useState(
    existing?.date ?? defaultDate ?? moment().format(DATE_FORMAT),
  );
  const [allDay, setAllDay] = useState(existing?.allDay ?? false);
  const [from, setFrom] = useState(existing?.from ?? defaultFrom ?? "09:00");
  const [to, setTo] = useState(existing?.to ?? defaultTo ?? "10:00");
  const [location, setLocation] = useState(existing?.location ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");

  const [attendees, setAttendees] = useState<Attendee[]>(
    existing?.attendees.filter((a) => !a.optional) ?? [],
  );
  const [optionalAttendees, setOptionalAttendees] = useState<Attendee[]>(
    existing?.attendees.filter((a) => a.optional) ?? [],
  );
  const [quoteId, setQuoteId] = useState<number | undefined>(existing?.quoteId);

  // Estimates belonging to the first attendee who is a known Lead with quotes.
  const quoteOptions = useMemo(() => {
    for (const a of [...attendees, ...optionalAttendees]) {
      const customer = leadsByEmail?.get(a.email);
      if (customer && customer.quotes.length > 0) {
        return customer.quotes;
      }
    }
    return [];
  }, [attendees, optionalAttendees, leadsByEmail]);

  const [existingAttachments, setExistingAttachments] = useState<
    BookingAttachment[]
  >(existing?.attachments ?? []);
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const timeInvalid = !allDay && from >= to;
  const isValid = title.trim().length > 0 && !timeInvalid;

  const handleSave = () => {
    if (!isValid) {
      return;
    }
    const booking: Booking = {
      id: existing?.id ?? genId(),
      title: title.trim(),
      type,
      date,
      from: allDay ? "00:00" : from,
      to: allDay ? "23:59" : to,
      allDay,
      location: location.trim() || undefined,
      description: description.trim() || undefined,
      attendees: [...attendees, ...optionalAttendees],
      attachments: existingAttachments,
      quoteId,
      // Preserve meeting lifecycle fields managed by the Meetings dashboard.
      meetingLink: existing?.meetingLink,
      vendorNote: existing?.vendorNote,
      status: existing?.status,
      quote: existing?.quote,
      createdByName: existing?.createdByName,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
    };
    onSave(booking, newFiles);
  };

  return (
    <>
      <Modal onClose={onClose}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box
            sx={{
              width: { xs: "88vw", sm: 480 },
              maxHeight: "86vh",
              overflowY: "auto",
            }}
          >
            <ModalHeader isEdit={isEdit} onClose={onClose} />

            <Stack spacing={2}>
              <Field label={t("booking.title")}>
                <MUITextField
                  fullWidth
                  size="small"
                  placeholder={t("booking.title_placeholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <DateTimeSection
                date={date}
                onDateChange={setDate}
                allDay={allDay}
                onAllDayChange={setAllDay}
                from={from}
                onFromChange={setFrom}
                to={to}
                onToChange={setTo}
                timeInvalid={timeInvalid}
              />

              <EventTypeSelect value={type} onChange={setType} />

              <Field label={t("booking.location")}>
                <MUITextField
                  fullWidth
                  size="small"
                  placeholder={t("booking.location_placeholder")}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </Field>

              <Field label={t("booking.attendees")}>
                <AttendeeInput
                  value={attendees}
                  onChange={setAttendees}
                  suggestions={leadSuggestions}
                  leadsByEmail={leadsByEmail}
                  onViewQuotes={(email) =>
                    setViewingQuotes(leadsByEmail?.get(email) ?? null)
                  }
                  onLeadPicked={(address) => {
                    if (!location.trim()) {
                      setLocation(address);
                    }
                  }}
                />
              </Field>

              <Field label={t("booking.optional")}>
                <AttendeeInput
                  value={optionalAttendees}
                  onChange={setOptionalAttendees}
                  suggestions={leadSuggestions}
                  optional
                  leadsByEmail={leadsByEmail}
                  onViewQuotes={(email) =>
                    setViewingQuotes(leadsByEmail?.get(email) ?? null)
                  }
                />
              </Field>

              {quoteOptions.length > 0 && (
                <Field label={t("booking.estimate")}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={quoteId ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQuoteId(v === "" ? undefined : Number(v));
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("booking.none")}</em>
                    </MenuItem>
                    {quoteOptions.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.estimateNumber
                          ? `#${q.estimateNumber}`
                          : `Quote ${q.id}`}
                        {q.chargerModel ? ` · ${q.chargerModel}` : ""}
                        {q.totalCost != null
                          ? ` · ${formatMoney(q.totalCost)}`
                          : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </Field>
              )}

              <Field label={t("booking.details")}>
                <MUITextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  placeholder={t("booking.details_placeholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <AttachmentsSection
                existingAttachments={existingAttachments}
                newFiles={newFiles}
                onAddFiles={(files) =>
                  setNewFiles((prev) => [...prev, ...files])
                }
                onRemoveExisting={(index) =>
                  setExistingAttachments((prev) =>
                    prev.filter((_, i) => i !== index),
                  )
                }
                onRemoveNew={(index) =>
                  setNewFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            </Stack>

            <ModalFooter
              isEdit={isEdit}
              isValid={isValid}
              saving={saving}
              onClose={onClose}
              onSave={handleSave}
            />
          </Box>
        </LocalizationProvider>
      </Modal>

      {viewingQuotes && (
        <QuoteDetailsDialog
          customer={viewingQuotes}
          onClose={() => setViewingQuotes(null)}
        />
      )}
    </>
  );
};

export default BookingModal;
